import React, { useState, useEffect } from 'react';
import './homepage.css';
import themeManager from './themeManager.js';
import { useContent } from './hooks/useContent';
import { enrichSocialLinks } from './api/socialIcons';
import GumballIcon from './components/GumballIcon';
import { MobileMenuLinks } from './components/MenuNav';
import HamburgerButton from './components/HamburgerButton';
import StoryHeading from './components/StoryHeading';

function renderHeadline(title) {
  return title.split('\n').map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

function Homepage() {
  const [currentTheme, setCurrentTheme] = useState(themeManager.getCurrentTheme());
  const { content } = useContent();
  const socialLinks = enrichSocialLinks(content.socials);

  useEffect(() => {
    const checkTheme = () => setCurrentTheme(themeManager.getCurrentTheme());
    const interval = setInterval(checkTheme, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.remove('daytime', 'nighttime');
    document.body.classList.add(currentTheme);

    const hamburger = document.querySelector('.hamburger');
    const panel = document.querySelector('.menu-panel');
    const scrim = document.querySelector('.menu-scrim');
    if (!hamburger || !panel || !scrim) return;

    const menuLinks = panel.querySelectorAll('.menu-link');
    const anchorLinks = document.querySelectorAll('.site-nav a[href^="#"], .menu-link[href^="#"]');

    function openMenu() {
      document.body.classList.add('menu-open');
      hamburger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      scrim.hidden = false;
    }

    function closeMenu() {
      document.body.classList.remove('menu-open');
      hamburger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      scrim.hidden = true;
    }

    function toggleMenu() {
      document.body.classList.contains('menu-open') ? closeMenu() : openMenu();
    }

    function scrollToHash(e, href) {
      if (!href?.startsWith('#')) return false;
      e.preventDefault();
      const target = document.querySelector(href);
      if (target) target.scrollIntoView({ behavior: 'smooth' });
      return true;
    }

    function onHamburgerClick() { toggleMenu(); }

    function onHamburgerKey(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      }
    }

    function onEscape(e) {
      if (e.key === 'Escape' && document.body.classList.contains('menu-open')) {
        closeMenu();
        hamburger.focus();
      }
    }

    function onAnchorClick(e) {
      const href = e.currentTarget.getAttribute('href');
      if (scrollToHash(e, href)) closeMenu();
    }

    hamburger.addEventListener('click', onHamburgerClick);
    scrim.addEventListener('click', closeMenu);
    hamburger.addEventListener('keydown', onHamburgerKey);
    document.addEventListener('keydown', onEscape);
    anchorLinks.forEach((link) => link.addEventListener('click', onAnchorClick));
    menuLinks.forEach((link) => {
      link.addEventListener('click', () => {
        if (!link.getAttribute('href')?.startsWith('#')) closeMenu();
      });
    });

    return () => {
      hamburger.removeEventListener('click', onHamburgerClick);
      scrim.removeEventListener('click', closeMenu);
      hamburger.removeEventListener('keydown', onHamburgerKey);
      document.removeEventListener('keydown', onEscape);
      anchorLinks.forEach((link) => link.removeEventListener('click', onAnchorClick));
    };
  }, [currentTheme]);

  const isDaytime = currentTheme === 'daytime';
  const story = content.aboutStory;

  return (
    <div className="App homepage">
      <header className="site-header">
        <a href="/" className="site-logo" aria-label="Gumball Buddies home">
          <img src="/imgs/gum_logo.png" alt="Gumball Buddies" className="site-logo-img" />
        </a>

        <nav className="site-nav" aria-label="Main navigation">
          <a href="#about">About</a>
          <a href="#roadmap">Roadmap</a>
          <a href="/announcements">Announcements</a>
          <a href="/media">Media</a>
          <a href="#socials">Community</a>
        </nav>

        <a href="#socials" className="site-header-cta">Join Us</a>

        <HamburgerButton />
      </header>

      <nav className="menu-panel" aria-hidden="true">
        <div className="menu-panel-inner">
          <p className="menu-brand">Gumball Buddies</p>
          <ul className="menu-list">
            <MobileMenuLinks
              onHomepage
              extraItems={[
                { href: '/roadmap', label: 'Full Roadmap', icon: 'list' },
                { href: '/announcements', label: 'Announcements', icon: 'megaphone' },
                { href: '/media', label: 'Media', icon: 'media' },
              ]}
            />
          </ul>
        </div>
      </nav>

      <div className="menu-scrim" hidden />

      <main className="site-main">
        {/* Hero */}
        <section className="hero-section">
          {isDaytime ? (
            <div className="sky" aria-hidden="true">
              <img src="/imgs/cloud.png" alt="" className="cloud" loading="lazy" decoding="async" />
              <img src="/imgs/cloud.png" alt="" className="cloud" loading="lazy" decoding="async" />
              <img src="/imgs/cloud.png" alt="" className="cloud" loading="lazy" decoding="async" />
              <span className="gumball gumball-1" />
              <span className="gumball gumball-2" />
              <span className="gumball gumball-3" />
            </div>
          ) : (
            <div className="night-sky" aria-hidden="true">
              <span className="night-bubble night-bubble-1" />
              <span className="night-bubble night-bubble-2" />
              <span className="night-bubble night-bubble-3" />
              <span className="night-bubble night-bubble-4" />
              <span className="night-moon"><GumballIcon name="moon" size={36} /></span>
            </div>
          )}

          <div className="hero-inner">
            <div className="hero-content">
              <p className="hero-eyebrow">{content.hero.eyebrow}</p>
              <div className="hero-logo-wrap">
                <img src="/imgs/gum_logo.png" alt="Gumball Buddies" className="hero-logo" />
              </div>
              <h1 className="hero-headline">{renderHeadline(content.hero.title)}</h1>
              <p className="hero-tagline">{content.hero.lead}</p>
              <div className="hero-ctas">
                <a href="/roadmap" className="cta-btn cta-primary">Explore Roadmap</a>
                <a href="#socials" className="cta-btn cta-secondary">Join the Community</a>
              </div>
            </div>
          </div>
        </section>

        {/* Announcements */}
        {content.announcements.length > 0 && (
          <section className="announcements-section" aria-label="Announcements">
            <div className="section-inner">
              <header className="announcements-section-header">
                <span className="section-eyebrow">Latest News</span>
                <a href="/announcements" className="announcements-view-all">View all announcements</a>
              </header>
              <div className="announcements-list">
                {content.announcements.map((a) => (
                  <article key={a.id} className="announcement-card glass-card">
                    {a.image_url ? (
                      <div className="announcement-card-image">
                        <img src={a.image_url} alt="" loading="lazy" />
                      </div>
                    ) : (
                      <span className="announcement-icon"><GumballIcon name="megaphone" size={28} /></span>
                    )}
                    <div>
                      {a.category && <span className={`announcement-category category-${a.category}`}>{a.category}</span>}
                      <h2 className="announcement-title">{a.title}</h2>
                      <p className="announcement-body">{a.preview_text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* About */}
        <section id="about" className="section about-section">
          <div className="section-inner">
            <header className="section-header">
              <span className="section-eyebrow">{content.aboutHeader.eyebrow}</span>
              <h2 className="bubble-title section-title">
                <span className="word-about">About</span>{' '}
                <span className="word-gumball">Gumball</span>{' '}
                <span className="word-buddies">Buddies</span>
              </h2>
              <p className="section-lead">{content.aboutHeader.lead}</p>
            </header>

            <div className="pillar-grid">
              {content.pillars.map((p) => (
                <article key={p.title} className="pillar-card">
                  <span className="pillar-icon"><GumballIcon name={p.icon} size={32} /></span>
                  <h3 className="pillar-title">{p.title}</h3>
                  <p className="pillar-text">{p.text}</p>
                </article>
              ))}
            </div>

            <figure className="about-evolution-wrap">
              <img
                src={story.evolution_image}
                alt="Gumball Buddies evolution — from meme coin to full cartoon ecosystem"
                className="about-evolution-img"
                loading="lazy"
                decoding="async"
              />
            </figure>

            <article className="story-card glass-card">
              <div className="bubble-deco bubble-deco-left" aria-hidden="true" />
              <div className="bubble-deco bubble-deco-right" aria-hidden="true" />
              <div className="about-body">
                <p>{story.intro}</p>
                <StoryHeading title={story.mission_title} icon={story.mission_icon} />
                <p>{story.mission}</p>
                <StoryHeading title={story.building_title} icon={story.building_icon} />
                <ul className="about-list">
                  {(story.building_items || []).map((item) => (
                    <li key={item.label}><strong>{item.label}</strong> — {item.text}</li>
                  ))}
                </ul>
                <StoryHeading title={story.audience_title} icon={story.audience_icon} />
                <p>{story.audience}</p>
              </div>
            </article>
          </div>
        </section>

        {/* Roadmap preview */}
        <section id="roadmap" className="section roadmap-section">
          <div className="section-inner">
            <header className="section-header">
              <span className="section-eyebrow">{content.roadmapHeader.eyebrow}</span>
              <h2 className="section-title-plain">{content.roadmapHeader.title}</h2>
              <p className="section-lead">{content.roadmapHeader.lead}</p>
            </header>

            <div className="roadmap-grid">
              {content.roadmapPreview.map((item) => (
                <article key={item.phase} className="roadmap-card">
                  <span className="roadmap-phase">{item.phase}</span>
                  <h3 className="roadmap-card-title">{item.title}</h3>
                  <p className="roadmap-card-desc">{item.desc}</p>
                </article>
              ))}
            </div>

            <div className="section-cta-row">
              <a href="/roadmap" className="cta-btn cta-primary">View Full Roadmap</a>
            </div>
          </div>
        </section>

        {/* Community */}
        <section id="socials" className="section community-section">
          <div className="section-inner">
            <header className="section-header">
              <span className="section-eyebrow">{content.communityHeader.eyebrow}</span>
              <h2 className="section-title-plain">{content.communityHeader.title}</h2>
              <p className="section-lead">{content.communityHeader.lead}</p>
            </header>

            <ul className="socials-grid">
              {socialLinks.map((s) => (
                <li key={s.label}>
                  <a
                    className={`social-btn ${s.cls}`}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Gumballbuddies on ${s.label}`}
                  >
                    <span className="icon" aria-hidden="true">{s.icon}</span>
                    <span className="label">{s.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-inner">
          <a href="/" className="footer-logo" aria-label="Gumball Buddies home">
            <img src="/imgs/gum_logo.png" alt="" />
          </a>
          <nav className="footer-nav" aria-label="Footer navigation">
            <a href="#about">About</a>
            <a href="#roadmap">Roadmap</a>
            <a href="/roadmap">Full Roadmap</a>
            <a href="/announcements">Announcements</a>
            <a href="/media">Media</a>
            <a href="#socials">Community</a>
          </nav>
          <div className="footer-socials">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                className={`footer-social ${s.cls}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                <span className="icon" aria-hidden="true">{s.icon}</span>
              </a>
            ))}
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Gumball Buddies. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Homepage;
